import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { AppEnvironment, MessagingTopology } from '@internal/open-catalog';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { ConnectionLineType, Controls, Edge, Node, ReactFlow, useEdgesState, useNodesState } from '@xyflow/react';
import '@xyflow/react/dist/base.css';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { cloudAmqpConfigRef } from '../../../../../../apis';
import { CloudAmqpConfig } from '../../../../../../configs';
import { FetchMqStatsService } from '../../../../../../services/FetchMqStatsService';
import { layoutWithELK, MessagingNodeData, MessagingNodeTypeWithStatus, parseTopology, useFlowLayoutStyles } from '../flow';
import { BindingsPanel } from './panel.bindings';
import { OptionsPanel } from './panel.options';
import { SelectedPanel } from './panel.selected';
import { StatusPanel } from './panel.status';

function NodeDetailsPanel(props: { data: MessagingNodeData }) {

  const { data } = props;

  const environments = useApi(cloudAmqpConfigRef);

  const [expanded, setExpanded] = useState<string>('');
  const [hasBindings, setHasBindings] = useState(false);

  const handleAccordionStateChange = (panel: any) => (_event: ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const [externalLink, setExternalLink] = useState('');

  useEffect(() => {
    
    const env = environments.find((environment: CloudAmqpConfig) => environment.connection.vhost === data.vhost);
    
    if (env && data.link) {
      setExternalLink(data.link(env.connection.url));
    }

    const isQueue = data.type === 'queue';
    
    if (isQueue || data.scope === 'external') {
      if (isQueue) {
        const name = data.spec.name;
        setHasBindings(!name.endsWith('.wait') && !name.endsWith('.parking-lot') && !name.endsWith('.overseer'));
      } else {
        setHasBindings(true);
      }
    } else {
      if ((data.spec as any).bindings?.length > 0) {
        setHasBindings(true);
      } else {
        setHasBindings(false);
      }
    }

  }, [data, environments, setHasBindings]);

  return (<>
    <SelectedPanel data={data} icon={data.icon} externalLink={externalLink} />
    {data.type === 'queue' && (
      <OptionsPanel data={data} expanded={expanded} onAccordionStateChange={handleAccordionStateChange} />
    )}
    <StatusPanel data={data} expanded={expanded} autoRefresh={data.refresh} onAccordionStateChange={handleAccordionStateChange} />
    {hasBindings && (
      <BindingsPanel data={data} expanded={expanded} onAccordionStateChange={handleAccordionStateChange} />
    )}
  </>);
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridContainer: {
      height: '100%'
    },
    flowContainer: {
      width: '100vw',
      height: '55vh',
      display: 'flex'
    },
    flowContainerFullScreen: {
      width: '100vw',
      height: 'calc(100% + 16px)',
      display: 'flex',
      '& div[class|="makeStyles-floatToolbar"]': {
        right: theme.spacing(3),
        '& div[class|="MuiAccordionDetails"]': {
          maxHeight: '58vh'
        }
      }
    },
    floatToolbar: {
      position: 'absolute',
      width: '25%',
      right: theme.spacing(6),
      zIndex: 10001,
      '& div[class|="MuiAccordionDetails"]': {
        maxHeight: '28vh'
      }
    }
  })
);

export function TopologyView(props: {
  topology: MessagingTopology,
  autoRefresh: boolean,
  env: AppEnvironment,
  fullScreen: boolean,
  focusMode: boolean,
  searchValue: string,
}) {

  const [selected, setSelected] = useState<any>();
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([] as any);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const classes = useStyles();
  const classesFlowLayout = useFlowLayoutStyles();

  const { topology, autoRefresh, env, fullScreen, focusMode, searchValue } = props;

  useEffect(() => {
    setSelected(undefined);
  }, [topology]);

  const onPaneClick = useCallback((_: React.MouseEvent) => {

    const animatedEdges = (edges || []).map((e: Edge) => {
      e.animated = false
      return e;
    });

    onEdgesChange(animatedEdges as any);
    setSelected(undefined);

  }, [edges, onEdgesChange, setSelected]);

  const nodeEdgesToAnimate = useCallback((selectedNode: Node, edgesToAnimate?: Edge[]) => {
    return (edgesToAnimate || edges || []).map((e: Edge) => {
      e.selected = false;
      e.animated = e.source === selectedNode.id || e.target === selectedNode.id;
      return e;
    });
  }, [edges]);

  const onNodeClick = useCallback((_: React.MouseEvent, selectedNode: Node) => {

    const selectedNodes = (nodes || [])
      .map((n: Node) => {
        n.selected = (n.id === selectedNode.id);
        return n;
      });

    const animatedEdges = nodeEdgesToAnimate(selectedNode);

    onNodesChange(selectedNodes as any);
    onEdgesChange(animatedEdges as any);

    setSelected(selectedNode);

  }, [nodes, onNodesChange, onEdgesChange, nodeEdgesToAnimate, setSelected]);

  const [mouseDown, setMouseDown] = useState(false);
  const redrawNodes = useCallback((genericEvent: any) => {
    if (!mouseDown && genericEvent.type === 'pointermove') {
      return;
    }
    setNodes([...nodes] as any);
  }, [nodes, setNodes, mouseDown]);

  useAsync(async () => {

    const { vhost } = topology || {};

    const url = await discoveryApi.getBaseUrl('proxy');

    const statsService = new FetchMqStatsService(url, fetchApi, env, vhost);

    const { nodes: parsedNodes, edges: parsedEdges } = parseTopology(
      topology, autoRefresh, statsService, focusMode, searchValue
    );

    const layouted = await layoutWithELK(parsedNodes, parsedEdges, {
      rootNode: classesFlowLayout.rootNodeWrapper,
      node: classesFlowLayout.nodeWrapper,
      edge: classesFlowLayout.edgeWrapper
    });

    if (layouted.nodes && layouted.nodes.length > 0) {
      const start = layouted.nodes[0];
      start.selected = true;
      nodeEdgesToAnimate(start, layouted.edges);
      setSelected(start);
    }

    setNodes(layouted.nodes as any);
    setEdges(layouted.edges as any);

  }, [topology, discoveryApi, fetchApi, setNodes, setEdges, setSelected, autoRefresh, focusMode, searchValue]);

  const onInit = (instace: any) => {
    setReactFlowInstance(instace);
  };

  useEffect(() => {
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView();
      }
    }, 100);
  }, [reactFlowInstance, fullScreen, focusMode, topology]);

  return (<Grid container className={classes.gridContainer}>
    <Grid item xs sm={12} className={fullScreen ? classes.flowContainerFullScreen : classes.flowContainer}>
      {nodes && edges && (
        <ReactFlow fitView
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={{ default: MessagingNodeTypeWithStatus } as any}
          connectionLineType={ConnectionLineType.SmoothStep}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onPointerDown={() => setMouseDown(true)}
          onPointerUp={() => setMouseDown(false)}
          onWheelCapture={redrawNodes}
          onPaneMouseMove={redrawNodes}
          onInit={onInit}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
      )}
      {selected && (<div className={classes.floatToolbar}>
        <NodeDetailsPanel data={selected.data as any} />
      </div>)}
    </Grid>
  </Grid>);
}
